// All the useful bits of code copied from here:
// https://css-tricks.com/making-an-audio-waveform-visualizer-with-vanilla-javascript/

import { useEffect } from "react";
import styles from "./waveformGenerator.module.css";

export const WaveformGenerator = ({
  totalBars,
  url,
  waveformData,
  setWaveformData,
}) => {
  useEffect(() => {
    const audioContext = new AudioContext();

    fetch(url)
      .then((response) => response.arrayBuffer())
      .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
      .then((audioBuffer) => visualize(audioBuffer));

    const visualize = (audioBuffer) => {
      const filtered = filterData(audioBuffer, totalBars);
      const normalised = normalizeData(filtered);
      setWaveformData(normalised);
    };
  }, [totalBars, url, setWaveformData]);

  return (
    <div className={styles.panel}>
      <div className={styles.waveform}>
        {waveformData && waveformData.map((wd, i) => <Bar key={i} size={wd} />)}
      </div>
    </div>
  );
};

const Bar = ({ size }) => {
  const background = "rgba(0,0,0,0.5)";
  const fullBarHeight = size * 80;

  return (
    <div className={styles.barHolder} style={{ height: 80 }}>
      <div
        className={styles.bar}
        style={{ height: fullBarHeight, background }}
      />
    </div>
  );
};

/**
 * Filters the AudioBuffer retrieved from an external source
 * @param {AudioBuffer} audioBuffer the AudioBuffer from drawAudio()
 * @returns {Array} an array of floating point numbers
 */
const filterData = (audioBuffer, totalBars) => {
  const rawData = audioBuffer.getChannelData(0); // We only need to work with one channel of data
  const samples = totalBars; // Number of samples we want to have in our final data set
  const blockSize = Math.floor(rawData.length / samples); // the number of samples in each subdivision
  const filteredData = [];
  for (let i = 0; i < samples; i++) {
    let blockStart = blockSize * i; // the location of the first sample in the block
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum = sum + Math.abs(rawData[blockStart + j]); // find the sum of all the samples in the block
    }
    filteredData.push(sum / blockSize); // divide the sum by the block size to get the average
  }
  return filteredData;
};

/**
 * Normalizes the audio data to make a cleaner illustration
 * @param {Array} filteredData the data from filterData()
 * @returns {Array} an normalized array of floating point numbers
 */
const normalizeData = (filteredData) => {
  const multiplier = Math.pow(Math.max(...filteredData), -1);
  return filteredData.map((n) => n * multiplier);
};
